import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Alert,
  Paper,
  Grid
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { apiCall } from '../utils/api';
import { trackUserAction } from '../utils/analytics';

const CreateRunbook = ({ onBack, onRunbookCreated, familyId, onCreateWithAI }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    steps: [''],
    emergency_contacts: [{ name: '', phone: '' }]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStepChange = (index, value) => {
    const newSteps = [...formData.steps];
    newSteps[index] = value;
    setFormData(prev => ({
      ...prev,
      steps: newSteps
    }));
  };

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, '']
    }));
  };

  const removeStep = (index) => {
    if (formData.steps.length > 1) {
      const newSteps = formData.steps.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        steps: newSteps
      }));
    }
  };

  const handleContactChange = (index, field, value) => {
    const newContacts = [...formData.emergency_contacts];
    newContacts[index][field] = value;
    setFormData(prev => ({
      ...prev,
      emergency_contacts: newContacts
    }));
  };

  const addContact = () => {
    setFormData(prev => ({
      ...prev,
      emergency_contacts: [...prev.emergency_contacts, { name: '', phone: '' }]
    }));
  };

  const removeContact = (index) => {
    if (formData.emergency_contacts.length > 1) {
      const newContacts = formData.emergency_contacts.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        emergency_contacts: newContacts
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(''); // Clear any previous errors

    try {
      // Filter out empty steps and contacts
      const cleanedData = {
        ...formData,
        steps: formData.steps.filter(step => step.trim() !== ''),
        emergency_contacts: formData.emergency_contacts.filter(
          contact => contact.name.trim() !== '' || contact.phone.trim() !== ''
        )
      };

      // Validate that we have at least one step after filtering
      if (cleanedData.steps.length === 0) {
        setError('Please add at least one step to your runbook.');
        return;
      }

      const result = await apiCall(`/api/families/${familyId}/runbooks`, {
        method: 'POST',
        body: JSON.stringify(cleanedData),
      });

      console.log('Runbook created successfully:', result);
      
      // Track successful runbook creation
      trackUserAction('create_runbook_success', {
        runbook_category: cleanedData.category,
        steps_count: cleanedData.steps.length
      });
      
      // Wait for the runbooks to be refreshed before navigating back
      await onRunbookCreated();
      onBack();
    } catch (error) {
      console.error('Error creating runbook:', error);
      setError(error.message || 'Failed to create runbook. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Create New Runbook
          </Typography>
        </Box>

        {/* AI Creation Option */}
        <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}
                >
                  <AddIcon sx={{ fontSize: 24, color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="h6" component="h3" gutterBottom>
                    Create with AI
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Describe what you need in plain English and let AI generate a custom template for you
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => onCreateWithAI ? onCreateWithAI() : onBack()}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
              >
                Generate with AI
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {/* Basic Information Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Basic Information
              </Typography>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Car Breakdown Emergency"
                  variant="outlined"
                />

                <TextField
                  fullWidth
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Vehicle, Personal, Home"
                  variant="outlined"
                />

                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  placeholder="Brief description of what this runbook covers"
                  multiline
                  rows={3}
                  variant="outlined"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Steps Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  Steps
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addStep}
                  size="small"
                >
                  Add Step
                </Button>
              </Box>
              
              <Stack spacing={2}>
                {formData.steps.map((step, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <TextField
                        fullWidth
                        label={`Step ${index + 1}`}
                        value={step}
                        onChange={(e) => handleStepChange(index, e.target.value)}
                        placeholder="Describe what to do in this step"
                        multiline
                        rows={2}
                        variant="outlined"
                        size="small"
                      />
                      {formData.steps.length > 1 && (
                        <IconButton
                          onClick={() => removeStep(index)}
                          color="error"
                          size="small"
                          sx={{ mt: 0.5 }}
                        >
                          <RemoveIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>

          {/* Emergency Contacts Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  Emergency Contacts (Optional)
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addContact}
                  size="small"
                >
                  Add Contact
                </Button>
              </Box>
              
              <Stack spacing={2}>
                {formData.emergency_contacts.map((contact, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          label="Contact Name"
                          value={contact.name}
                          onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                          placeholder="e.g., AAA Roadside"
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          label="Phone Number"
                          value={contact.phone}
                          onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                          placeholder="e.g., 1-800-222-4357"
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        {formData.emergency_contacts.length > 1 && (
                          <IconButton
                            onClick={() => removeContact(index)}
                            color="error"
                            size="small"
                          >
                            <RemoveIcon />
                          </IconButton>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={onBack}
              size="large"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={isSubmitting}
              size="large"
            >
              {isSubmitting ? 'Creating...' : 'Create Runbook'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default CreateRunbook;